/* eslint-disable no-console */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import { Autocomplete, Box, Button, Grid, TextField, Typography, } from '@mui/material';
import { createCycle,  cycleCreatorsearch,  CycleData,  cycleFields, getmedicationprotocol, getProcedure, getValidationSchema, initialValues, MedicationProtocolResponse, PROCEDURES_WITHOUT_LMP, TouchedFields } from './CreateNewCycleHelper';
import styles from './CycleOverViewList.module.css';
import { RequiredLabel } from '../../utils/commonUtils';
import { ReactComponent as More } from '../../assets/SVG/close-circle-greay.svg';
import { ConfirmationModal } from '../confirmationModal';
import { useLoader } from '../loaderProvider/loader';
import { useAlert } from '../alertProvider';
import { usePatientContext } from '../SubPatinetProvider';
import { useClinic } from '../ClinicProvider';
import { useParams } from 'react-router-dom';
import { patientDetailsObservable } from '../../services/sharedService';
import { useMutation } from '@tanstack/react-query';
interface CreateNewCycleProps {
  onClose: () => void;
}

const CreateNewCycle: React.FC<CreateNewCycleProps> = ({ onClose }) => {
  const { t } = useTranslation('cycleOverview_config');
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  type ValidateForm = () => Promise<Record<string, string>>;
  const { patientId } = usePatientContext();
  const { addAlert } = useAlert();
  const { showLoader, hideLoader } = useLoader();
  const { ClinicId } = useClinic(); 
  const { id } = useParams();

  const [patientDetailsId, setPatientDetailsId] = useState<number>();
  
  
  
  useEffect(() => {
    const subscription = patientDetailsObservable.subscribe((value) => {
      const getPatientId = ['SPERM_DONOR', 'OOCYTE_DONOR', 'SURROGATE'].includes(value?.patient_type ?? '')
        ? patientId
        : id ?? '';
      const safePatientId = getPatientId !== undefined && getPatientId !== null && getPatientId !== '' ? Number(getPatientId) : 0;
      setPatientDetailsId(safePatientId);
    });
    return () => subscription.unsubscribe();
  }, [id, patientId]);

  const [CycleCreatorID, setCycleCreatorID] = useState<string>('');
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleCreateCycle = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, validateForm: ValidateForm, setTouched: (touched: TouchedFields) => void, values: CycleData, proceduresList: any[]) => {
    validateForm().then((errors: Record<string, string>) => {
      // Check if current procedure requires LMP date
      const currentProcedure = proceduresList.find((p) => p.id === values.procedure);
      const procedureName = currentProcedure?.procedures || '';
      const lmpRequired = !PROCEDURES_WITHOUT_LMP.includes(procedureName);
      
      // Remove lmp_date error if not required for this procedure
      if (!lmpRequired && errors.lmp_date) {
        delete errors.lmp_date;
      }
      
      const isValid = Object.keys(errors).length === 0;
      const missingFields: string[] = [];
      Object.keys(cycleFields).forEach((field) => {
        if (cycleFields[field as keyof typeof cycleFields] && errors[field]) {
          missingFields.push(field);
        }
      });
  
      if (isValid) {
        setOpenConfirmDialog(true);
      } else {
        setTouched(cycleFields);
      }
    });
  };
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [CycleCoordinator, setCycleCoordinator] = useState<any[]>([]);
  // const [cycleCreator, setCycleCreator] = useState<any[]>([]);
  useEffect(() => {
    cycleCreatorsearch('DOCTOR') 
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((response: any) => {
        showLoader();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const formattedCycleCreators = response.objects.map((creator: any) => ({
          ...creator,
          fullName: `${creator.first_name} ${creator.last_name}`  
        }));
        setCycleCoordinator(formattedCycleCreators);
      })
      .catch((err) => {
        console.error(err); 
      })
      .finally(() => {
        hideLoader();
      });
  }, [hideLoader,showLoader]);

  
   

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [CycleProvider, setCycleProvider] = useState<any[]>([]);
  useEffect(() => {
    cycleCreatorsearch('NURSE') 
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((response: any) => {
        showLoader();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const formattedCycleCreators = response.objects.map((creator: any) => ({
          ...creator,
          fullName: `${creator.first_name} ${creator.last_name}`  
        }));
        // setCycleCoordinator(formattedCycleCreators);
        setCycleProvider(formattedCycleCreators);
      })
      .catch((err) => {
        console.error(err); 
      })
      .finally(() => {
        hideLoader();
      });
  }, [hideLoader, showLoader]);

    
  
  // TanStack Query mutation for creating cycle
  const createCycleMutation = useMutation({
    mutationFn: (cycleDataWithStatus: CycleData & { status: string; patient: number | undefined; cycle_creator: string }) => 
      createCycle(cycleDataWithStatus),
    onSuccess: () => {
      addAlert(t('CycleCreatedSuccess', 'Cycle created successfully'), 'success');
      onClose();
    },
    onError: (error: { response?: { data?: { errors?: Array<{ detail: string }> } } }) => {
      if (error?.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const errorMessage = error.response.data.errors.map((err: { detail: string }) => err.detail).join(', ');
        addAlert(errorMessage, 'error');
      } else {
        console.error('Error creating cycle:', error);
        addAlert(t('CycleCreatedError', 'Error creating cycle'), 'error');
      }
    },
  });

  const handleSubmit = (values: CycleData) => {
    const cycleDataWithStatus = { ...values, status: 'ACTIVE', patient: patientDetailsId, cycle_creator: CycleCreatorID };
    createCycleMutation.mutate(cycleDataWithStatus);
  };
  
  


  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [procedures, setProcedures] = useState<any[]>([]); 

  useEffect(() => {
    if (!patientDetailsId) {
      return;
    }
    getProcedure(Number(patientDetailsId))
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((data: any) => {
        setProcedures(data); 
      })
      .catch((error) => {
        console.error('Error fetching procedure:', error); 
      });

      
  }, [patientDetailsId]);



  const [medicationProtocol, setMedicationProtocol] = useState<{ id: number; name: string }[]>([]);
  useEffect(() => {
    getmedicationprotocol(ClinicId)
      .then((data: MedicationProtocolResponse) => {
        if (data && data.objects) {
          const protocols = data.objects.map((obj) => ({
            id: obj.id,
            name: obj.name,
          }));
          setMedicationProtocol(protocols);
        }
      })
      .catch((error) => {
        console.error('Error fetching medication protocols:', error);
      });
  }, [ClinicId]);

  
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={getValidationSchema()}
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      onSubmit={(values) => {}}
    >
      {({ handleChange, handleBlur, values, errors, touched, validateForm, setTouched }) => 
      {
        return      (
          <Form>

            <Box display="flex" justifyContent="space-between" alignItems="center" padding="1rem">
              <Typography variant="h6" className={styles.CycleList}>
                {t('CreateNewCycle')}
              </Typography>
              <More style={{ cursor: 'pointer' }} onClick={onClose} />


            </Box>
            <Grid container spacing={2} justifyContent="center">
              <Grid item xs={12} sm={4}>
                {/* <Autocomplete
                id="procedure"
                options={procedures.filter(option => option.procedures)}
                getOptionLabel={(option) => option.procedures || ''}
                value={procedures.find((p) => p.id === values.procedure) || null}
                onChange={(_, value) => {
                  handleChange({ target: { name: 'procedure', value: value?.id || '' } });
                  const selectedProcedure = procedures.find((p) => p.id === value?.id);
                  if (selectedProcedure) {
                    handleChange({ target: { name: 'indication', value: selectedProcedure.indication || '' } });
                    const doctorName = selectedProcedure.doctor_name || '';
                    handleChange({ target: { name: 'doctor_name', value: doctorName } });
                    const doctorId = selectedProcedure.doctor || '';
                    setCycleCreatorID(doctorId);
                    handleChange({ target: { name: 'cycle_creator', value: doctorName || '' } });
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('CycleName')}
                    value={values.procedure ? procedures.find((p) => p.id === values.procedure)?.procedures || '' : ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.procedure && !!errors.procedure}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        type: 'search',
                      },
                    }}
                  />
                )}
              /> */}


                <Autocomplete
                  id="procedure"
                  options={procedures ? procedures.filter(option => option?.procedures_label) : []}
                  getOptionLabel={(option) => option?.procedures_label || ''}
                  value={procedures?.find((p) => p.id === values?.procedure) || null}
                  onChange={(_, value) => {
                    handleChange({ target: { name: 'procedure', value: value?.id || '' } });
                    const selectedProcedure = procedures.find((p) => p.id === value?.id);
                    if (selectedProcedure) {
                      handleChange({ target: { name: 'indication', value: selectedProcedure.indication || '' } });
                      handleChange({ target: { name: 'indication_label', value: selectedProcedure.indication_label || '' } });
                      const doctorName = selectedProcedure.doctor_name || '';
                      handleChange({ target: { name: 'doctor_name', value: doctorName } });
                      const doctorId = selectedProcedure.doctor || '';
                      setCycleCreatorID(doctorId);
                      handleChange({ target: { name: 'cycle_creator', value: doctorName || '' } });
                    
                      // Clear lmp_date if procedure doesn't require LMP date
                      if (PROCEDURES_WITHOUT_LMP.includes(selectedProcedure.procedures)) {
                        handleChange({ target: { name: 'lmp_date', value: null } });
                      }
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={RequiredLabel(t('CycleName'), true)}
                      value={values.procedure ? procedures.find((p) => p.id === values.procedure)?.procedures_label || '' : ''}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={touched.procedure && !!errors.procedure}
                    />
                  )}
                />
              </Grid>


              <Grid item xs={12} sm={4}>
                <TextField
                  name="indication"
                  label={t('Indication')}
                  fullWidth
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.indication_label || ''}
                  disabled  
                  sx={{
                    '& .MuiInputBase-root': {
                      backgroundColor:  '#f5f5f5' ,
                    },
                  }}
                />

              </Grid>

              <Grid item xs={12} sm={4}>
                <Autocomplete
                  id="medication_protocol"
                  options={medicationProtocol}
                  getOptionLabel={(option) => option.name || ''} 
                  value={
                    medicationProtocol.find(
                      (option) => String(option.id) === String(values.medication_protocol)
                    ) || null
                  }
                  onChange={(_, value) =>
                    handleChange({
                      target: {
                        name: 'medication_protocol',
                        value: value?.id || '', 
                      },
                    })
                  }
                  onBlur={handleBlur}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={RequiredLabel(t('MedicationProtocol'), true)}
                      name="medication_protocol"
                      error={touched.medication_protocol && !!errors.medication_protocol}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&.Mui-focused fieldset': {
                            borderColor: '#0C7DED',
                          },
                        },
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} justifyContent="center" className="mt-2">
              <Grid item xs={12} sm={4}>

                <TextField
                  name="cycle_creator"
                  label={t('CycleCreator')}
                  fullWidth
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.cycle_creator || ''}  
                  disabled  
                  sx={{
                    '& .MuiInputBase-root': {
                      backgroundColor:  '#f5f5f5' ,
                    },
                  }}
                />


                {/* <Autocomplete
                id="cycle_creator"
                disableClearable
                options={cycleCreator}
                getOptionLabel={(option) => option.fullName}
                value={cycleCreator.find(option => option.id === values.cycle_creator) || null}
                onChange={(_, value) => {
                  // Set the cycle creator id and doctor name in the form
                  handleChange({ target: { name: 'cycle_creator', value: value?.id || '' } });
    
                  // Optionally, set additional data if needed
                  if (value) {
                    handleChange({ target: { name: 'doctor_name', value: value.fullName || '' } });
                    handleChange({ target: { name: 'doctor', value: value.id || '' } }); // Set the doctor id
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cycle Creator"
                    value={values.cycle_creator ? cycleCreator.find(option => option.id === values.cycle_creator)?.fullName : ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        type: 'search',
                      },
                    }}
                    sx={{
                      '& .MuiInputBase-root': {
                        backgroundColor: '#f5f5f5',
                      },
                    }}
                    error={touched.cycle_creator && !!errors.cycle_creator}
                  />
                )}
              /> */}


              </Grid>

              <Grid item xs={12} sm={4}>
                <Autocomplete
                  id="cycle_coordinator"
                  options={CycleCoordinator}
                  getOptionLabel={(option) => option.fullName || ''}
                  value={CycleCoordinator.find(option => option.id === values.cycle_coordinator) || null}
                  onChange={(_, value) => {
                    handleChange({ target: { name: 'cycle_coordinator', value: value?.id || '' } });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('CycleCoordinator')}
                      onBlur={handleBlur}
                      error={touched.cycle_coordinator && !!errors.cycle_coordinator}
                      helperText={touched.cycle_coordinator && errors.cycle_coordinator}
                    />
                  )}
                />
              </Grid>




              <Grid item xs={12} sm={4}>
                <Autocomplete
                  id="provider_name"
                  options={CycleProvider}
                  getOptionLabel={(option) => option.fullName || ''}
                  value={CycleProvider.find(option => option.id === values.cycle_provider) || null}
                  onChange={(_, value) => {
                    handleChange({ target: { name: 'cycle_provider', value: value?.id || '' } });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('CycleProvider')}
                      onBlur={handleBlur}
                      error={touched.cycle_provider && !!errors.cycle_provider}
                      helperText={touched.cycle_provider && errors.cycle_provider}
                    />
                  )}
                />
              </Grid>

            </Grid>


            <Grid container spacing={2} justifyContent="center" className="mt-2">
              <Grid item xs={12} sm={4}>
                <TextField
                  name="cycle_start_date"
                  label={RequiredLabel(t('CycleStartDate'), true)}
                  fullWidth
                  onChange={handleChange}
                  onBlur={handleBlur}
                  type='date'
                  InputLabelProps={{ shrink: true }}
                  value={values.cycle_start_date || ''}
                  error={touched.cycle_start_date && !!errors.cycle_start_date}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: '#0C7DED',
                      },
                    },
                  }}
                // inputProps={{
                //   max: new Date().toISOString().split('T')[0], 
                // }}
                />

              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  name="cycle_end_date"
                  label={t('CycleEndDate')}
                  fullWidth
                  type='date'
                  InputLabelProps={{ shrink: true }}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.cycle_end_date || ''}
                  inputProps={{
                    min: values.cycle_start_date ? new Date(new Date(values.cycle_start_date).getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: '#0C7DED',
                      },
                    },
                  }}
                />

              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  name="lmp_date"
                  label={RequiredLabel(
                    t('LMPDate'), 
                    !PROCEDURES_WITHOUT_LMP.includes(procedures.find((p) => p.id === values.procedure)?.procedures || '')
                  )}
                  fullWidth
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.lmp_date || null}
                  disabled={PROCEDURES_WITHOUT_LMP.includes(procedures.find((p) => p.id === values.procedure)?.procedures || '')}
                  inputProps={{
                    min: values.cycle_start_date ? new Date(values.cycle_start_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0], 
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: '#0C7DED',
                      },
                    },
                    '& .MuiInputBase-root': {
                      backgroundColor: PROCEDURES_WITHOUT_LMP.includes(procedures.find((p) => p.id === values.procedure)?.procedures || '') ? '#f5f5f5' : 'transparent',
                    },
                  }}
                  error={touched.lmp_date && !!errors.lmp_date && !PROCEDURES_WITHOUT_LMP.includes(procedures.find((p) => p.id === values.procedure)?.procedures || '')}
                />
              </Grid>


            </Grid>
            <Grid container spacing={2} justifyContent="start" className="mt-2">
              {/* <Grid item xs={12} sm={4}>
              <TextField
                name="cycle_warning"
                label="Cycle Warning"
                fullWidth
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.cycle_warning || ''}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: '#0C7DED',
                    },
                  },
                }}
              />

            </Grid> */}

              <Grid item xs={12} sm={4}>
                <TextField
                  name="remarks"
                  label={t('Remarks')}
                  fullWidth
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.remarks || ''}
                  multiline
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: '#0C7DED',
                      },
                    },
                  }}

                />


              </Grid>
            </Grid>

            <hr className="mt-4" />


            <div>
              <Box display="flex" justifyContent="flex-end" alignItems="center" padding="1rem">
                <Button
                  variant="contained"
                  color="primary"
                  className={styles.AddNewCycle}
                  disabled={createCycleMutation.isPending}
                  onClick={(e) => handleCreateCycle(e, validateForm, setTouched, values, procedures)}
                >
                  <span className={styles.AddNewCycleText} >{createCycleMutation.isPending ? t('Creating', 'Creating...') : t('CreateNewCycle')}</span>
                </Button>
              </Box>
            </div>
            <ConfirmationModal
              open={openConfirmDialog}
              handleClose={() => setOpenConfirmDialog(false)}
              onConfirm={() => {
                setOpenConfirmDialog(false);
                handleSubmit(values);
              }}
              title={t('CreateNewCycle')}
              contentMessage={t('CreateNewCycleConfirm')}
              type={'save'}
            />
          </Form>
        );
      }
      }


    </Formik>
  );
};

export default CreateNewCycle;
